import type { SummaryListItemType } from '@/types'
import { formatNumberWithThousandSeparators, formatUsageRatio, getCurrencyForCountry } from '@/helpers/format'
import { getSubmissionDifficultyLabel } from '@/helpers/submission-difficulty'
import {
  type PdfNode,
  type ColumnWidths,
  getCurrentLocale,
  getPdfMake,
  setupPdfMakeFonts,
  createListSection,
  trimTrailingSectionSpacing,
  buildFooter,
  getSharedStyles,
  getPdfAuthor,
} from './pdf-shared'

interface SubmissionPlanningStartRoleData {
  difficultyFactor: number | null
  effortDeterminingCosts: number | null
  serviceShare: number | null
  offeredHours: number | null
}

interface SubmissionPlanningStart {
  year: number | null
  constructionCosts: number | null
  roleData: Record<string, SubmissionPlanningStartRoleData>
}

interface SubmissionPlanningEndRoleData {
  difficultyFactor: number | null
  effortDeterminingCosts: number | null
  serviceShare: number | null
  invoicedHours: number | null
  effectiveHours: number | null
}

interface SubmissionPlanningEnd {
  year: number | null
  constructionCosts: number | null
  roleData: Record<string, SubmissionPlanningEndRoleData>
}

export interface SubmissionFormSnapshot {
  planningStart: SubmissionPlanningStart
  planningEnd: SubmissionPlanningEnd
}

export interface SubmissionMetadata {
  projectTitle: string
  validRoles: string[]
  country: string
}

const SUBMISSION_COLUMNS: ColumnWidths = { label: '55%', value: '45%' }

export class PdfMakeSubmissionGenerator {
  private formData: SubmissionFormSnapshot
  private metadata: SubmissionMetadata
  private wtSummary: (...path: string[]) => string
  private wtGeneral: (...path: string[]) => string

  constructor(
    formData: SubmissionFormSnapshot,
    metadata: SubmissionMetadata,
    wtSummary: (...path: string[]) => string,
    wtGeneral: (...path: string[]) => string
  ) {
    this.formData = formData
    this.metadata = metadata
    this.wtSummary = wtSummary
    this.wtGeneral = wtGeneral
  }

  async generate(): Promise<string> {
    const pdfMake = await getPdfMake()
    await setupPdfMakeFonts()
    const filename = this.getFilename()
    const docDefinition = this.buildDocumentDefinition()
    pdfMake.createPdf(docDefinition).download(filename)
    return filename
  }

  private getFilename(): string {
    const dateStr = new Date().toISOString().split('T')[0]
    const projectName = this.metadata.projectTitle
    return projectName
      ? `CoreValue_${this.wtSummary('projectSubmission', 'title').replace(/\s/g, '_')}_${projectName.replace(/\s/g, '_')}_${dateStr}.pdf`
      : `CoreValue_${this.wtSummary('projectSubmission', 'title').replace(/\s/g, '_')}_${dateStr}.pdf`
  }

  private getDocumentTitle(): string {
    return (
      this.wtSummary('projectSubmission', 'documentTitle') || `${this.wtSummary('projectSubmission', 'title')} ValueApp`
    )
  }

  private buildDocumentDefinition(): Record<string, unknown> {
    const today = Intl.DateTimeFormat(getCurrentLocale(), { dateStyle: 'long' }).format(new Date())
    const documentTitle = this.getDocumentTitle()

    const content: PdfNode[] = [
      { text: documentTitle, style: 'documentTitle' },
      { text: this.metadata.projectTitle, style: 'projectTitle', margin: [0, 12, 0, 4] },
      {
        text: (this.wtSummary('pdf', 'description', 'generatedLabel') || 'PDF generated by ValueApp on {date}').replace(
          '{date}',
          today
        ),
        fontSize: 11,
        color: '#737373',
        margin: [0, 6, 0, 20],
      },
    ]

    const sections: PdfNode[] = []
    const addSection = (section: PdfNode | null) => {
      if (section) sections.push(section)
    }

    this.buildPlanningStartSections().forEach(addSection)
    this.buildPlanningEndSections().forEach(addSection)

    content.push({
      stack: trimTrailingSectionSpacing(sections),
    })

    return {
      pageSize: 'A4',
      pageMargins: [40, 46, 40, 46],
      defaultStyle: {
        font: 'DINPro',
        fontSize: 10,
        color: '#404040',
      },
      info: {
        title: this.wtSummary('projectSubmission', 'title'),
        subject: documentTitle,
        author: getPdfAuthor(),
      },
      footer: buildFooter(this.wtSummary, today),
      styles: getSharedStyles(),
      content,
    }
  }

  private buildPlanningStartSections(): (PdfNode | null)[] {
    const currency = getCurrencyForCountry(this.metadata.country)
    const ps = this.formData.planningStart
    const sections: (PdfNode | null)[] = []

    // Main items: year, construction costs
    const mainItems: SummaryListItemType[] = []

    if (ps.year != null) {
      mainItems.push({
        title: this.wtSummary('projectSubmission', 'planningStart', 'year'),
        value: String(ps.year),
      })
    }

    if (ps.constructionCosts != null) {
      mainItems.push({
        title: this.wtSummary('projectSubmission', 'planningStart', 'constructionCosts'),
        value: `${formatNumberWithThousandSeparators(ps.constructionCosts)} ${currency}`,
      })
    }

    sections.push(
      createListSection(
        this.wtSummary('projectSubmission', 'planningStart', 'title'),
        mainItems,
        'default',
        SUBMISSION_COLUMNS
      )
    )

    // Per-role subtables
    for (const role of this.metadata.validRoles) {
      const rd = ps.roleData?.[role]
      if (!rd) continue

      const roleItems: SummaryListItemType[] = []

      if (rd.difficultyFactor != null) {
        roleItems.push({
          title: this.getDifficultyLabel(role, 'planningStart'),
          value: String(rd.difficultyFactor),
        })
      }

      if (rd.effortDeterminingCosts != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningStart', 'effortDeterminingCosts'),
          value: `${formatNumberWithThousandSeparators(rd.effortDeterminingCosts)} ${currency}`,
        })
      }

      if (rd.serviceShare != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningStart', 'serviceShare'),
          value: `${formatUsageRatio(rd.serviceShare)}%`,
        })
      }

      if (rd.offeredHours != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningStart', 'offeredHours'),
          value: `${formatNumberWithThousandSeparators(rd.offeredHours)} h`,
        })
      }

      sections.push(
        createListSection(
          this.wtGeneral('roleParts', role),
          roleItems,
          'default',
          SUBMISSION_COLUMNS,
          'subSectionTitle'
        )
      )
    }

    return sections
  }

  private buildPlanningEndSections(): (PdfNode | null)[] {
    const currency = getCurrencyForCountry(this.metadata.country)
    const pe = this.formData.planningEnd
    const sections: (PdfNode | null)[] = []

    // Main items: year, construction costs
    const mainItems: SummaryListItemType[] = []

    if (pe.year != null) {
      mainItems.push({
        title: this.wtSummary('projectSubmission', 'planningEnd', 'year'),
        value: String(pe.year),
      })
    }

    if (pe.constructionCosts != null) {
      mainItems.push({
        title: this.wtSummary('projectSubmission', 'planningEnd', 'constructionCosts'),
        value: `${formatNumberWithThousandSeparators(pe.constructionCosts)} ${currency}`,
      })
    }

    sections.push(
      createListSection(
        this.wtSummary('projectSubmission', 'planningEnd', 'title'),
        mainItems,
        'default',
        SUBMISSION_COLUMNS
      )
    )

    // Per-role subtables
    for (const role of this.metadata.validRoles) {
      const rd = pe.roleData[role]
      if (!rd) continue

      const roleItems: SummaryListItemType[] = []

      if (rd.difficultyFactor != null) {
        roleItems.push({
          title: this.getDifficultyLabel(role, 'planningEnd'),
          value: String(rd.difficultyFactor),
        })
      }

      if (rd.effortDeterminingCosts != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningEnd', 'effortDeterminingCosts'),
          value: `${formatNumberWithThousandSeparators(rd.effortDeterminingCosts)} ${currency}`,
        })
      }

      if (rd.serviceShare != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningEnd', 'serviceShare'),
          value: `${formatUsageRatio(rd.serviceShare)}%`,
        })
      }

      if (rd.invoicedHours != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningEnd', 'invoicedHours'),
          value: `${formatNumberWithThousandSeparators(rd.invoicedHours)} h`,
        })
      }

      if (rd.effectiveHours != null) {
        roleItems.push({
          title: this.wtSummary('projectSubmission', 'planningEnd', 'effectiveHours'),
          value: `${formatNumberWithThousandSeparators(rd.effectiveHours)} h`,
        })
      }

      sections.push(
        createListSection(
          this.wtGeneral('roleParts', role),
          roleItems,
          'default',
          SUBMISSION_COLUMNS,
          'subSectionTitle'
        )
      )
    }

    return sections
  }

  private getDifficultyLabel(
    role: string,
    section: 'mainUsage' | 'planningStart' | 'planningEnd' = 'mainUsage'
  ): string {
    return getSubmissionDifficultyLabel(role, this.wtSummary, section)
  }
}
